import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import useAuth from '../hooks/useAuth';
import SortFilter, { sortItems } from '../components/SortFilter';

const emptyTeacher = { name: '', phone: '', email: '', branch_id: '', is_active: true };

const countryOptions = [
  { code: '+27', flag: '🇿🇦', name: 'South Africa (+27)' },
  { code: '+91', flag: '🇮🇳', name: 'India (+91)' },
  { code: '+1', flag: '🇺🇸', name: 'USA (+1)' },
  { code: '+44', flag: '🇬🇧', name: 'UK (+44)' },
  { code: '+971', flag: '🇦🇪', name: 'UAE (+971)' },
  { code: '+61', flag: '🇦🇺', name: 'Australia (+61)' },
  { code: '+65', flag: '🇸🇬', name: 'Singapore (+65)' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia (+966)' },
];

export default function TeachersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentBranch, user } = useAuth();

  const [teachers, setTeachers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sortOption, setSortOption] = useState('a-z');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(searchParams.get('branch_id') || '');
  const [selectedClass, setSelectedClass] = useState('');
  const [filterClasses, setFilterClasses] = useState([]);
  const [filterClassesLoading, setFilterClassesLoading] = useState(false);
  const [form, setForm] = useState(emptyTeacher);
  const [countryCode, setCountryCode] = useState('+27');
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  // Assign Branch state
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [assignBranchId, setAssignBranchId] = useState('');
  const [isPrimaryBranch, setIsPrimaryBranch] = useState(true);
  const [assignClassesList, setAssignClassesList] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [assignClassesLoading, setAssignClassesLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load branch classes for filter whenever branch changes or page loads for center manager
  useEffect(() => {
    const activeBranchId = user?.role === 'admin'
      ? selectedBranch
      : (currentBranch?.branch_id || user?.branch_id || selectedBranch);

    if (activeBranchId) {
      loadBranchClassesForFilter(activeBranchId);
    } else {
      setFilterClasses([]);
      setSelectedClass('');
    }
  }, [selectedBranch, user, currentBranch]);

  async function loadBranchClassesForFilter(branchId) {
    setFilterClassesLoading(true);
    try {
      const res = await api.get(`/branches/${branchId}/classes`);
      const activeClasses = (res.data.data || []).filter(c => c.is_active === 1);
      setFilterClasses(activeClasses);
    } catch (err) {
      setFilterClasses([]);
    } finally {
      setFilterClassesLoading(false);
    }
  }

  // Fetch classes when assignBranchId changes
  useEffect(() => {
    const targetBranch = user?.role === 'admin' ? assignBranchId : (currentBranch?.branch_id || assignBranchId);
    if (isAssignModalOpen && targetBranch) {
      fetchBranchClassesForAssign(targetBranch);
    } else {
      setAssignClassesList([]);
      setSelectedClassIds([]);
    }
  }, [assignBranchId, isAssignModalOpen]);

  async function fetchBranchClassesForAssign(branchId) {
    setAssignClassesLoading(true);
    try {
      const [classesRes, teacherClassesRes] = await Promise.all([
        api.get(`/branches/${branchId}/classes`),
        selectedTeacher ? api.get(`/admin/teachers/${selectedTeacher.id}/classes`) : Promise.resolve({ data: { data: [] } })
      ]);
      const availableClasses = classesRes.data.data || [];
      const teacherAssignedClasses = teacherClassesRes.data.data || [];
      const alreadyAssignedIds = teacherAssignedClasses.map(c => c.id);

      setAssignClassesList(availableClasses);
      setSelectedClassIds(alreadyAssignedIds);
    } catch (err) {
      console.error('Error loading branch classes for assignment:', err);
    } finally {
      setAssignClassesLoading(false);
    }
  }


  // Add/Edit Teacher modal classes state
  const [formBranchClasses, setFormBranchClasses] = useState([]);
  const [formSelectedClassIds, setFormSelectedClassIds] = useState([]);
  const [formClassesLoading, setFormClassesLoading] = useState(false);

  useEffect(() => {
    loadBranches();
  }, []);

  // Load branch classes whenever form.branch_id changes in Add/Edit modal
  useEffect(() => {
    if (isAddModalOpen && form.branch_id) {
      loadBranchClassesForTeacherModal(form.branch_id);
    } else {
      setFormBranchClasses([]);
      if (!editingTeacher) setFormSelectedClassIds([]);
    }
  }, [form.branch_id, isAddModalOpen]);

  async function loadBranchClassesForTeacherModal(branchId) {
    setFormClassesLoading(true);
    try {
      const [classesRes, teacherClassesRes] = await Promise.all([
        api.get(`/branches/${branchId}/classes`),
        editingTeacher ? api.get(`/admin/teachers/${editingTeacher.id}/classes`) : Promise.resolve({ data: { data: [] } })
      ]);
      const availableClasses = (classesRes.data.data || []).filter(c => c.is_active === 1);
      const teacherAssigned = teacherClassesRes.data.data || [];
      const assignedIds = teacherAssigned.map(c => c.id);

      setFormBranchClasses(availableClasses);
      if (editingTeacher) {
        setFormSelectedClassIds(assignedIds);
      }
    } catch (err) {
      console.error('Error loading branch classes:', err);
    } finally {
      setFormClassesLoading(false);
    }
  }


  useEffect(() => {
    fetchTeachers();
  }, [selectedBranch, selectedClass]);

  async function loadBranches() {
    try {
      const res = await api.get('/admin/branches');
      setBranches(res.data.data || []);
    } catch (err) {}
  }

  async function fetchTeachers() {
    setLoading(true);
    setError('');
    try {
      const params = { role: 'teacher' };
      const activeBranchId = user?.role === 'admin' 
        ? selectedBranch 
        : (currentBranch?.branch_id || user?.branch_id || selectedBranch);

      if (activeBranchId) params.branch_id = activeBranchId;
      if (selectedClass) params.class_id = selectedClass;

      const response = await api.get('/users', { params });
      setTeachers(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load teachers');
    } finally {
      setLoading(false);
    }
  }

  const openAddModal = () => {
    setEditingTeacher(null);
    const initialBranch = currentBranch?.branch_id
      ? String(currentBranch.branch_id)
      : (branches.length > 0 ? String(branches[0].id) : '');

    setForm({
      ...emptyTeacher,
      branch_id: initialBranch
    });
    setFormSelectedClassIds([]);
    setFormBranchClasses([]);
    setCountryCode('+27');
    setIsAddModalOpen(true);
  };

  const openEditModal = (teacher) => {
    setEditingTeacher(teacher);
    setFormSelectedClassIds([]);
    setFormBranchClasses([]);

    let rawPhone = teacher.phone || '';
    let foundCode = '+27';
    let digitsOnly = rawPhone.replace(/\D/g, '');

    for (const option of countryOptions) {
      const cCodeDigits = option.code.replace('+', '');
      if (rawPhone.startsWith(option.code) || digitsOnly.startsWith(cCodeDigits)) {
        foundCode = option.code;
        if (digitsOnly.startsWith(cCodeDigits)) {
          digitsOnly = digitsOnly.slice(cCodeDigits.length);
        }
        break;
      }
    }

    const primaryBranchId = teacher.assigned_branches && teacher.assigned_branches.length > 0
      ? String(teacher.assigned_branches[0].branch_id)
      : (currentBranch?.branch_id ? String(currentBranch.branch_id) : (branches.length > 0 ? String(branches[0].id) : ''));

    setCountryCode(foundCode);
    setForm({
      name: teacher.name || '',
      phone: digitsOnly.slice(0, 10),
      email: teacher.email || '',
      branch_id: primaryBranchId,
      is_active: Number(teacher.is_active) === 1,
    });
    setIsAddModalOpen(true);
  };

  const openAssignModal = (teacher) => {
    setSelectedTeacher(teacher);
    if (user?.role === 'admin') {
      setAssignBranchId('');
    } else {
      setAssignBranchId(currentBranch?.branch_id ? String(currentBranch.branch_id) : '');
    }
    setIsPrimaryBranch(true);
    setIsAssignModalOpen(true);
  };

  const closeModal = () => {
    setIsAddModalOpen(false);
    setEditingTeacher(null);
    setForm(emptyTeacher);
    setFormSelectedClassIds([]);
    setFormBranchClasses([]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setError('');
    setSuccessMessage('');

    if (!form.email || !form.email.trim()) {
      setError('Email address is required');
      return;
    }

    let finalPhone = '';
    if (form.phone) {
      const cleanPhone = form.phone.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        setError('Please enter a valid 10-digit mobile phone number');
        return;
      }
      finalPhone = `${countryCode}${cleanPhone}`;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: finalPhone || undefined,
        email: form.email ? form.email.trim() : undefined,
        branch_id: form.branch_id || undefined,
        class_ids: formSelectedClassIds,
        is_active: form.is_active ? 1 : 0,
      };

      if (editingTeacher) {
        await api.put(`/users/${editingTeacher.id}`, payload);
        setSuccessMessage('Teacher updated successfully');
      } else {
        await api.post('/users', { ...payload, role: 'teacher' });
        setSuccessMessage('Teacher added successfully');
      }

      closeModal();
      fetchTeachers();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save teacher');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignBranch = async (event) => {
    event.preventDefault();
    const targetBranchId = user?.role === 'admin' ? assignBranchId : (currentBranch?.branch_id || assignBranchId);

    if (!targetBranchId) {
      setError('Please select a branch to assign');
      return;
    }
    setError('');
    setSuccessMessage('');
    try {
      await api.post('/admin/branches/assign-teacher-classes', {
        teacher_id: selectedTeacher.id,
        branch_id: parseInt(targetBranchId),
        is_primary: isPrimaryBranch,
        class_ids: selectedClassIds
      });
      const branchObj = branches.find(b => b.id === parseInt(targetBranchId)) || currentBranch;
      const branchName = branchObj ? branchObj.name : 'Branch';
      setSuccessMessage(`Branch "${branchName}" & assigned classes saved for "${selectedTeacher.name}" successfully`);
      setIsAssignModalOpen(false);
      fetchTeachers();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to assign branch/classes to teacher');
    }
  };

  const handleUnassignBranch = async (teacherId, branchId, branchName) => {
    if (!window.confirm(`Are you sure you want to remove ${branchName} assignment from this teacher?`)) return;
    setError('');
    setSuccessMessage('');
    try {
      await api.post('/admin/branches/unassign-teacher', {
        teacher_id: teacherId,
        branch_id: branchId,
      });
      setSuccessMessage('Branch assignment removed successfully');
      fetchTeachers();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to remove branch assignment');
    }
  };

  const toggleStatus = async (teacher) => {
    setError('');
    setSuccessMessage('');
    const isCurrentActive = Number(teacher.is_active) === 1;
    const newStatus = isCurrentActive ? 0 : 1;
    try {
      await api.put(`/users/${teacher.id}`, { is_active: newStatus });
      setSuccessMessage(`Teacher "${teacher.name}" is now ${newStatus === 1 ? 'Active' : 'Inactive'}`);
      fetchTeachers();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update status');
    }
  };

  const sortedTeachers = sortItems(teachers, sortOption, 'name', 'created_at');

  const filteredTeachers = sortedTeachers.filter((t) => {
    const isActive = Number(t.is_active) === 1;
    if (statusFilter === 'active' && !isActive) return false;
    if (statusFilter === 'inactive' && isActive) return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const matchName = t.name?.toLowerCase().includes(q);
      const matchPhone = t.phone?.toLowerCase().includes(q);
      const matchEmail = t.email?.toLowerCase().includes(q);
      if (!matchName && !matchPhone && !matchEmail) return false;
    }

    return true;
  });

  return (
    <div>
      <PageHeader
        title="Teacher Management"
        subtitle="Add teachers, manage login access, and enable accounts"
        action={
          <button type="button" className="primary-button" onClick={openAddModal} style={{ whiteSpace: 'nowrap' }}>
            + Add Teacher
          </button>
        }
      />
      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="page-header-row" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        {user?.role === 'admin' && (
          <select
            value={selectedBranch}
            onChange={(e) => {
              setSelectedBranch(e.target.value);
              setSelectedClass('');
            }}
            style={{
              padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
              fontSize: 14, minWidth: 160, cursor: 'pointer', background: '#ffffff'
            }}
          >
            <option value="">All branches</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}

        {/* Classes Filter Dropdown */}
        <select
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
          disabled={user?.role === 'admin' && !selectedBranch}
          style={{
            padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
            fontSize: 14, minWidth: 160,
            cursor: (user?.role === 'admin' && !selectedBranch) ? 'not-allowed' : 'pointer',
            background: (user?.role === 'admin' && !selectedBranch) ? '#f1f5f9' : '#ffffff',
            color: (user?.role === 'admin' && !selectedBranch) ? '#94a3b8' : '#1e293b'
          }}
        >
          <option value="">
            {filterClassesLoading
              ? 'Loading classes...'
              : (user?.role === 'admin' && !selectedBranch ? 'Select branch first' : 'All Classes')}
          </option>
          {filterClasses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}{c.section ? ` (${c.section})` : ''}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
            fontSize: 14, minWidth: 160, cursor: 'pointer', background: '#ffffff'
          }}
        >
          <option value="all">All Statuses (Active & Inactive)</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search teacher by name, phone, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 12px 8px 34px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: 14,
              minWidth: 200,
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              background: '#ffffff'
            }}
          />
          <span style={{ position: 'absolute', left: 10, color: '#94a3b8', fontSize: 13, pointerEvents: 'none' }}>
            🔍
          </span>
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: 8,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: 14,
                fontWeight: 700
              }}
            >
              ×
            </button>
          )}
        </div>

        <SortFilter sortOption={sortOption} onSortChange={setSortOption} />
      </div>

      {/* Add / Edit Teacher Modal */}
      <Modal isOpen={isAddModalOpen} onClose={closeModal} title={editingTeacher ? 'Edit Teacher' : 'Add Teacher'}>
        <form onSubmit={handleSubmit}>
          {(user?.role === 'admin' || editingTeacher) && (
            <label style={{ display: 'block', marginBottom: '12px' }}>
              Branch
              <select
                value={form.branch_id || ''}
                onChange={(e) => setForm((p) => ({ ...p, branch_id: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, marginTop: 4 }}
              >
                <option value="">Select Branch (Optional)</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Classes Selection in Selected Branch */}
          {form.branch_id && (
            <div style={{ marginBottom: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', color: '#334155' }}>
                Select Classes (Multiple selection allowed)
              </div>
              {formClassesLoading ? (
                <div style={{ fontSize: '12px', color: '#64748b' }}>Loading branch classes...</div>
              ) : formBranchClasses.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                  No classes created yet for this branch. Create classes from Branches page first.
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                  gap: '6px',
                  maxHeight: '130px',
                  overflowY: 'auto',
                  marginTop: '6px'
                }}>
                  {formBranchClasses.map((cls) => {
                    const isChecked = formSelectedClassIds.includes(cls.id);
                    return (
                      <label
                        key={cls.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          fontWeight: isChecked ? 600 : 400,
                          color: isChecked ? '#4338ca' : '#475569',
                          cursor: 'pointer',
                          background: isChecked ? '#e0e7ff' : '#ffffff',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          border: isChecked ? '1px solid #c7d2fe' : '1px solid #cbd5e1'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormSelectedClassIds(prev => [...prev, cls.id]);
                            } else {
                              setFormSelectedClassIds(prev => prev.filter(id => id !== cls.id));
                            }
                          }}
                        />
                        <span>{cls.name} {cls.section ? `(${cls.section})` : ''}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <label>
            Name
            <input placeholder="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
          </label>

          <label>
            Phone Number (10 digits)
            <div style={{ display: 'flex', gap: 6 }}>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                style={{ width: 110, padding: '8px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 13, background: '#f8fafc', cursor: 'pointer' }}
              >
                {countryOptions.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="10-digit mobile number"
                maxLength={10}
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                style={{ flex: 1 }}
              />
            </div>
            {form.phone && (
              <span style={{ fontSize: 11, color: form.phone.length === 10 ? '#16a34a' : '#dc2626', marginTop: 3, display: 'block', fontWeight: 500 }}>
                {form.phone.length === 10 ? '✓ Valid 10-digit number' : `Entered ${form.phone.length}/10 digits`}
              </span>
            )}
          </label>

          <label>
            Email <span style={{ color: '#ef4444' }}>*</span>
            <input
              placeholder="Email address (e.g. teacher@example.com)"
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              required
            />
          </label>

          <label className="form-checkbox" style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              name="is_active"
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#7c3aed' }}
            />
            <span style={{ fontSize: '14px', fontWeight: 600, color: form.is_active ? '#15803d' : '#b91c1c' }}>
              Active Teacher
            </span>
          </label>

          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={closeModal} disabled={isSubmitting}>Cancel</button>
            <button type="submit" className="primary-button" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : (editingTeacher ? 'Update Teacher' : 'Save Teacher')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Assign Branch Modal */}
      <Modal isOpen={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} title={`Assign Branch to "${selectedTeacher?.name}"`}>
        <form onSubmit={handleAssignBranch}>
          <label>
            Teacher Name
            <input
              type="text"
              value={selectedTeacher?.name || ''}
              disabled
              style={{ background: '#f1f5f9', color: '#475569', fontWeight: 600, cursor: 'not-allowed' }}
            />
          </label>

          <label>
            Branch to Assign
            {user?.role === 'admin' ? (
              <select
                value={assignBranchId}
                onChange={(e) => setAssignBranchId(e.target.value)}
                required
                style={{ padding: '8px 12px', width: '100%', borderRadius: 8, border: '1px solid #e5e7eb', marginTop: 4 }}
              >
                <option value="">Choose a branch</option>
                {branches
                  .filter(b => !selectedTeacher?.assigned_branches?.some(ab => ab.branch_id === b.id))
                  .map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))
                }
              </select>
            ) : (
              <div style={{
                padding: '10px 14px',
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#334155',
                marginTop: 4
              }}>
                📍 {currentBranch?.name || 'Current Branch'}
              </div>
            )}
          </label>

          {/* Classes Selection inside Branch */}
          <div style={{ marginTop: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px', color: '#334155' }}>
              Assign Classes (Optional)
            </div>
            {assignClassesLoading ? (
              <div style={{ fontSize: '12px', color: '#64748b' }}>Loading branch classes...</div>
            ) : assignClassesList.length === 0 ? (
              <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                {assignBranchId ? 'No classes created for this branch yet. You can create classes from the Branches page.' : 'Select a branch above to view available classes.'}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: '8px',
                maxHeight: '150px',
                overflowY: 'auto',
                padding: '8px',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                {assignClassesList.map((cls) => {
                  const isChecked = selectedClassIds.includes(cls.id);
                  return (
                    <label
                      key={cls.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: isChecked ? 600 : 400,
                        color: isChecked ? '#4338ca' : '#475569',
                        cursor: 'pointer',
                        background: isChecked ? '#e0e7ff' : '#ffffff',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: isChecked ? '1px solid #c7d2fe' : '1px solid #cbd5e1'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedClassIds(prev => [...prev, cls.id]);
                          } else {
                            setSelectedClassIds(prev => prev.filter(id => id !== cls.id));
                          }
                        }}
                      />
                      <span>{cls.name} {cls.section ? `(${cls.section})` : ''}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
            <input
              type="checkbox"
              checked={isPrimaryBranch}
              onChange={(e) => setIsPrimaryBranch(e.target.checked)}
            />
            Set as Primary Branch
          </label>

          <div className="modal-actions" style={{ marginTop: 24 }}>
            <button type="button" className="secondary-button" onClick={() => setIsAssignModalOpen(false)}>Cancel</button>
            <button type="submit" className="primary-button">Assign Branch</button>
          </div>
        </form>
      </Modal>



      {loading ? (
        <div>Loading teachers...</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Assigned Branches</th>
                <th>Status</th>
                <th>Created</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTeachers.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                    No teachers found for the selected branch/filter.
                  </td>
                </tr>
              ) : (
                filteredTeachers.map((t) => (
                  <tr key={t.id}>
                    <td
                      style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 500 }}
                      onClick={() => navigate(`/teachers/${t.id}`)}
                    >
                      {t.name || '-'}
                    </td>
                    <td>{t.phone || '-'}</td>
                    <td>{t.email || '-'}</td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {t.assigned_branches && t.assigned_branches.length > 0 ? (
                          t.assigned_branches.map((b) => {
                            const isDisabled = Number(b.is_active) === 0;
                            return (
                              <span key={b.branch_id} style={{
                                padding: '3px 10px',
                                background: isDisabled ? '#fee2e2' : (b.is_primary ? '#ede9fe' : '#f3f4f6'),
                                color: isDisabled ? '#dc2626' : (b.is_primary ? '#7c3aed' : '#4b5563'),
                                border: isDisabled ? '1px solid #fca5a5' : (b.is_primary ? '1px solid #ddd6fe' : '1px solid #e5e7eb'),
                                borderRadius: 12,
                                fontSize: 12,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: isDisabled ? 700 : 500
                              }}>
                                {b.name} {b.is_primary ? '★' : ''} {isDisabled ? '(Disabled)' : ''}
                                <button
                                  type="button"
                                  onClick={() => handleUnassignBranch(t.id, b.branch_id, b.name)}
                                  style={{
                                    border: 'none',
                                    background: 'none',
                                    color: '#ef4444',
                                    cursor: 'pointer',
                                    padding: '0 2px',
                                    fontSize: 12,
                                    fontWeight: 700
                                  }}
                                  title="Unassign branch from teacher"
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: 13 }}>No branches assigned</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {(() => {
                        const isActive = Number(t.is_active) === 1;
                        return (
                          <button
                            type="button"
                            onClick={() => toggleStatus(t)}
                            title={`Click to set as ${isActive ? 'Inactive' : 'Active'}`}
                            style={{
                              border: 'none',
                              borderRadius: '16px',
                              padding: '4px 12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
                              color: isActive ? '#15803d' : '#b91c1c',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <span style={{
                              width: '7px',
                              height: '7px',
                              borderRadius: '50%',
                              backgroundColor: isActive ? '#22c55e' : '#ef4444'
                            }} />
                            {isActive ? 'Active' : 'Inactive'}
                          </button>
                        );
                      })()}
                    </td>
                    <td>{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
                        <button
                          type="button"
                          className="secondary-button"
                          style={{ padding: '4px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}
                          onClick={() => openEditModal(t)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          style={{
                            padding: '4px 10px', fontSize: '12px', borderRadius: '6px',
                            border: '1px solid #c7d2fe', backgroundColor: '#e0e7ff', color: '#4338ca',
                            cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4,
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => openAssignModal(t)}
                          title={`Assign a branch to ${t.name}`}
                        >
                          🔗 Assign Branch
                        </button>
                        <button
                          type="button"
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            borderRadius: '6px',
                            border: Number(t.is_active) === 1 ? '1px solid #fca5a5' : '1px solid #86efac',
                            backgroundColor: Number(t.is_active) === 1 ? '#fef2f2' : '#f0fdf4',
                            color: Number(t.is_active) === 1 ? '#dc2626' : '#16a34a',
                            cursor: 'pointer',
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => toggleStatus(t)}
                        >
                          {Number(t.is_active) === 1 ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
